/* Each reservation gallery owns its timers, listeners and current slide. */
if (!customElements.get('c1-reserve-offer')) {
  customElements.define('c1-reserve-offer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cleanups = [];
      this.timer = null;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.slides = Array.from(this.querySelectorAll('[data-cro-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-cro-thumbnail]'));
      this.viewport = this.querySelector('[data-cro-viewport]');
      this.current = 0;
      this.autoplay = this.dataset.autoplay !== 'false';
      this.touchStart = null;
      this.touchActive = false;
      this.interval = Math.max(3000, Math.min(10000, Number(this.dataset.interval) || 3000));

      this.listen(document, 'shopify:section:unload', (event) => {
        if (String(event.detail?.sectionId) === this.dataset.sectionId || (event.target instanceof Element && event.target.contains(this))) {
          this.disconnect();
        }
      });
      this.initReveal();
      if (this.slides.length < 2) return;

      this.count = this.querySelector('[data-cro-count]');
      this.status = this.querySelector('[data-cro-status]');
      this.querySelector('[data-cro-thumbnails]').hidden = false;
      this.querySelector('[data-cro-controls]').hidden = false;
      const gallery = this.querySelector('.cro__card');
      gallery.classList.add('is-gallery-ready');
      this.viewport.tabIndex = 0;
      this.viewport.setAttribute('aria-label', 'Product images. Use the left and right arrow keys to change image.');

      this.thumbnails.forEach((button, index) => {
        this.listen(button, 'click', () => this.selectByUser(index));
      });
      this.listen(this.querySelector('[data-cro-previous]'), 'click', () => this.selectByUser(this.current - 1));
      this.listen(this.querySelector('[data-cro-next]'), 'click', () => this.selectByUser(this.current + 1));
      this.listen(gallery, 'keydown', (event) => {
        let next;
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') next = this.current + (rtl ? -1 : 1);
        if (event.key === 'ArrowLeft') next = this.current + (rtl ? 1 : -1);
        if (event.key === 'ArrowDown') next = this.current + 1;
        if (event.key === 'ArrowUp') next = this.current - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.slides.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        const moveThumbnailFocus = event.target.closest('[data-cro-thumbnail]');
        this.selectByUser(next);
        if (moveThumbnailFocus) this.thumbnails[this.current].focus({ preventScroll: true });
      });

      this.listen(this.viewport, 'touchstart', (event) => {
        this.touchActive = event.touches.length > 0;
        this.touchStart = event.touches.length === 1
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
          : null;
        this.syncTimer();
      }, { passive: true });
      this.listen(this.viewport, 'touchend', (event) => {
        const start = this.touchStart;
        this.touchStart = null;
        this.touchActive = (event.touches?.length || 0) > 0;
        if (!this.touchActive && start && event.changedTouches.length === 1) {
          const dx = event.changedTouches[0].clientX - start.x;
          const dy = event.changedTouches[0].clientY - start.y;
          const threshold = Math.max(30, Math.min(60, this.viewport.clientWidth * 0.1));
          if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.3) {
            const rtl = getComputedStyle(this).direction === 'rtl';
            this.selectByUser(this.current + ((dx < 0) !== rtl ? 1 : -1));
            return;
          }
        }
        this.syncTimer();
      }, { passive: true });
      this.listen(this.viewport, 'touchcancel', () => { this.touchStart = null; this.touchActive = false; this.syncTimer(); }, { passive: true });
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) {
          this.touchStart = null;
          this.touchActive = false;
        }
        this.syncTimer();
      });
      const motionChanged = () => {
        if (this.motion.matches) {
          this.revealAll();
        }
        this.syncTimer();
      };
      if (this.motion.addEventListener) {
        this.listen(this.motion, 'change', motionChanged);
      } else {
        this.motion.addListener(motionChanged);
        this.cleanups.push(() => this.motion.removeListener(motionChanged));
      }
      this.listen(this, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.selectByUser(index);
      });
      this.show(0);
      this.syncTimer();
    }

    listen(target, type, listener, options) {
      target.addEventListener(type, listener, options);
      this.cleanups.push(() => target.removeEventListener(type, listener, options));
    }

    initReveal() {
      if (this.dataset.animate !== 'true' || this.motion.matches || !('IntersectionObserver' in window)) {
        this.revealAll();
        return;
      }
      this.classList.add('is-reveal-ready');
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            this.observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });
      this.querySelectorAll('[data-cro-reveal]').forEach((element) => this.observer.observe(element));
    }

    revealAll() {
      this.observer?.disconnect();
      this.querySelectorAll('[data-cro-reveal]').forEach((element) => element.classList.add('is-in'));
    }

    selectByUser(index) {
      // Give the newly selected image a full interval, then continue rotating.
      this.show(index, true);
      this.syncTimer();
    }

    show(index, announce = false) {
      this.current = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        const selected = position === this.current;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', String(!selected));
        slide.inert = !selected;
      });
      this.thumbnails.forEach((button, position) => {
        const selected = position === this.current;
        button.setAttribute('aria-pressed', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      if (this.count) this.count.textContent = `${this.current + 1} / ${this.slides.length}`;
      if (announce && this.status) {
        this.status.textContent = `Image ${this.current + 1} of ${this.slides.length}: ${this.slides[this.current].dataset.slideLabel}`;
      }
    }

    syncTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (!this.initialized || this.slides.length < 2) return;
      if (!this.autoplay || this.motion.matches || this.touchActive || document.hidden) return;
      this.timer = window.setTimeout(() => {
        this.show(this.current + 1);
        this.syncTimer();
      }, this.interval);
    }

    disconnect() {
      window.clearTimeout(this.timer);
      this.timer = null;
      this.observer?.disconnect();
      this.cleanups?.forEach((cleanup) => cleanup());
      this.cleanups = [];
      this.initialized = false;
    }

    disconnectedCallback() {
      this.disconnect();
    }
  });
}
